import {
  faEye,
  faEyeSlash,
  faLink,
  faPen,
  faTrafficLight,
  faTrophy,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import React, { useState } from 'react';
import {
  KeywordTableColumn,
  KeywordTableColumnTypes,
} from '../../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { KeywordColumnPickerBag } from '../../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/useKeywordColumnPicker';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelMenuDivider from '../../../components/voxel/PopoverMenu/VoxelMenuDivider';
import VoxelMenuItemContainer from '../../../components/voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../../components/voxel/PopoverMenu/VoxelPopoverMenuItem';
import { VoxelText } from '../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import { DomainGq } from '../../../graphql/projects/models/DomainGq';
import EditSingleDomainForm from './EditSingleDomainForm';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';

export interface DomainItemProps {
  domain: DomainGq;
  deletingIds: number[];
  columnPickerBag: KeywordColumnPickerBag;
  handleDelete: (domain: DomainGq) => void;
  reload: () => Promise<any>;
}

const DomainItem: React.FC<DomainItemProps> = React.memo(
  ({ domain, deletingIds, columnPickerBag, handleDelete, reload }) => {


    const [editDomainOpen, setEditDomainOpen] = useState(false);
    const [editVisibleColumnsOpen, setEditVisibleColumnsOpen] = useState(false);

    const column = columnPickerBag.columnState.find(
      (cs) =>
        cs.id === domain.id &&
        cs.order !== undefined &&
        cs.type === KeywordTableColumnTypes.Rank
    );

    const beingDeleted = deletingIds.includes(domain.id);


    // Toggle Position
    const handleTogglePosition = (rankColumn: KeywordTableColumn) => {
      const maxOrder = chain(columnPickerBag.columnState)
        .maxBy((x) => x?.order)
        .value()?.order ?? 1;
      columnPickerBag.setColumnState((prev) =>
        prev.map((cs) => {
          if (cs.type === KeywordTableColumnTypes.Rank && cs.id === domain.id) {
            return {
              ...cs,
              order: rankColumn?.order === undefined ? maxOrder + 1 : undefined,
            };
          } else return cs;
        })
      )

    };

    // Toggle Traffic
    const handleToggleTraffic = (rankColumn: KeywordTableColumn) => {
      if (rankColumn.type === KeywordTableColumnTypes.Keyword) return;
      columnPickerBag.setColumnState((prev) =>
        chain(prev)
          .map((c) => {
            if (c.type === rankColumn.type && c.id === rankColumn.id)
              return { ...c, traffic: !c?.traffic };
            return c;
          })
          .orderBy((c) => c === undefined || c.order)
          .map((c, index) => {
            if (c.order === undefined) return c;
            if (c.order === index) return c;
            return { ...c, order: index };
          })
          .value()
      );
    };

    // Toggle URL
    const handleToggleUrl = (rankColumn: KeywordTableColumn) => {
      if (rankColumn.type === KeywordTableColumnTypes.Keyword) return;
      columnPickerBag.setColumnState((prev) =>
        chain(prev)
          .map((c) => {
            if (c.type === rankColumn.type && c.id === rankColumn.id) return { ...c, url: !c?.url };
            return c;
          })
          .orderBy((c) => c === undefined || c.order)
          .map((c, index) => {
            if (c.order === undefined) return c;
            if (c.order === index) return c;
            return { ...c, order: index };
          })
          .value()
      );
    };
    if (beingDeleted) {
      return null;
    }

    return (
      <>
        <div
          key={domain.id}
          className="w-full flex flex-col gap-1 justify-between "
        >
          <div className="w-full flex flex-row items-center gap-1.5">
            {/* Url */}
            <VoxelText variant="table" color={'dark'} noWrap ellipsis className="grow">
              <VoxelTooltipShort message={domain.protocol + domain.hostName} placement="top">
                <a
                  href={domain.protocol + domain.hostName}
                  target="_blank"
                  className="hover:underline"
                >
                  {domain.hostName}
                </a>
              </VoxelTooltipShort>
            </VoxelText>
            <div className="flex flex-row gap-0.5 items-center justify-self-end">
              {/* Edit domain */}
              {domain.type === DomainTypeGq.PROJECT && (
                <VoxelPopover
                  placement="bottom-start"
                  offsetPx={10}
                  open={editDomainOpen}
                  onOpenChange={setEditDomainOpen}
                >
                  <VoxelPopoverTrigger enableActive>
                    <VoxelIconButton
                      icon={<FontAwesomeIcon icon={faPen} size="sm" />}
                      color="white"
                      tooltip="Edit"
                      size="xxs"
                    />
                  </VoxelPopoverTrigger>
                  <VoxelPopoverContent>
                    <EditSingleDomainForm
                      domain={domain}
                      handleDelete={handleDelete}
                      handleClose={() => setEditDomainOpen(null)}
                      onSuccess={() => reload().then(() => setEditDomainOpen(null))}
                    />
                  </VoxelPopoverContent>
                </VoxelPopover>
              )}
              {/* Show / hide columns */}
              <VoxelPopover
                placement="bottom-start"
                offsetPx={10}
                open={editVisibleColumnsOpen}
                onOpenChange={setEditVisibleColumnsOpen} >
                <VoxelPopoverTrigger>
                  <VoxelIconButton
                    tooltip='Show/hide table columns'
                    icon={<FontAwesomeIcon icon={column?.order === undefined ? faEyeSlash : faEye} size="sm" />}
                    size="xxs"
                    color="white"
                  />
                </VoxelPopoverTrigger>
                <VoxelPopoverContent className='w-60 min-w-60'>
                  <VoxelMenuItemContainer size='md'>
                    <VoxelPopoverMenuItem
                      icon={<FontAwesomeIcon icon={faTrophy} />}
                      title={'Show Position'}
                      selected={column?.order !== undefined}
                      onClick={() => handleTogglePosition(column)}
                      closePopoverOnClick={false}
                    />
                    <VoxelMenuDivider />
                    <VoxelPopoverMenuItem
                      icon={<FontAwesomeIcon icon={faTrafficLight} className="ml-[3px]" />}
                      title={'Show Traffic'}
                      selected={column?.traffic}
                      onClick={() => handleToggleTraffic(column)}
                      disabled={beingDeleted || column?.order === undefined}
                      closePopoverOnClick={false}
                    />
                    <VoxelPopoverMenuItem
                      icon={<FontAwesomeIcon icon={faLink} />}
                      title={'Show Ranking URL'}
                      selected={column?.url}
                      onClick={() => handleToggleUrl(column)}
                      disabled={beingDeleted || column?.order === undefined}
                      closePopoverOnClick={false}
                    />
                  </VoxelMenuItemContainer>
                </VoxelPopoverContent>
              </VoxelPopover>
            </div>
          </div>
          {/* <div className="flex flex-row justify-between">
            <div className="flex flex-row gap-1">
              <VoxelSwitch
                color="primary"
                title="Traffic"
                size="xs"
                value={!!column?.traffic}
                onChange={() => handleToggleTraffic(column)}
              />
              <VoxelSwitch
                color="primary"
                title="Ranking url"
                size="xs"
                value={!!column?.url}
                onChange={() => handleToggleUrl(column)}
              />
            </div>
          </div> */}
        </div>
      </>
    );
  }
);

export default DomainItem;
